import { type ReactNode } from "react";
import { motion, useReducedMotion, type MotionProps } from "framer-motion";
import { ease, fadeInUpVariants } from "@/lib/motion";

interface FadeInProps {
  children: ReactNode;
  delay?: number;
  direction?: "up" | "down" | "none";
  className?: string;
  as?: keyof typeof motion;
  once?: boolean;
}

export function FadeIn({
  children,
  delay = 0,
  direction = "up",
  className,
  as = "div",
  once = true,
}: FadeInProps) {
  const reducedMotion = useReducedMotion();
  const MotionComponent = motion[as] as React.ComponentType<
    MotionProps & { className?: string }
  >;
  const variants =
    direction === "up"
      ? fadeInUpVariants
      : direction === "down"
        ? { hidden: { opacity: 0, y: -8 }, visible: { opacity: 1, y: 0 } }
        : { hidden: { opacity: 0 }, visible: { opacity: 1 } };

  return (
    <MotionComponent
      variants={variants}
      initial={reducedMotion ? false : "hidden"}
      animate="visible"
      transition={{ ...ease, delay: reducedMotion ? 0 : delay }}
      className={className}
      {...(once ? {} : { "data-animate": "repeat" })}
    >
      {children}
    </MotionComponent>
  );
}
